<template>
  <v-app>
    <d-header
      title="DEMRE"
      :logged="false"
      :show-side-icon="false"
    />
    <v-main>
      <v-container>
        <v-row justify="center" class="mt-2">
          <v-col cols="12" md="10">
            <v-card elevation="2">
              <v-card-title class="headline">
                <v-icon class="mr-2" large color="secondary">
                  folder_open
                </v-icon>
                <strong>PORTAL BASES DE DATOS</strong>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" class="body-1 mt-2 mb-4 text-justify">
                    El DEMRE de la Universidad de Chile pone a disposición del público general, las Bases de Datos (BBDD) del proceso de admisión a la educación universitaria, que contienen información desagregada a nivel de los individuos participantes del proceso de admisión.
                  </v-col>
                </v-row>
                <v-divider />
                <v-row>
                  <v-col cols="12" class="body-1 mt-4 mb-3 text-justify">
                    Esta entrega de datos es parte de nuestra política de transparencia activa, promoviendo el acceso rápido y sencillo a la información.
                    Si bien las BBDD están disponibles para todo público, es necesario que se cumpla con las normas éticas de manejo de datos.
                    <a :href="urlCondicionesUso" class="subrayado" target="_blank" rel="noopener noreferrer" @click="detalleInfoDialog = true">Ver aquí</a>.
                  </v-col>
                </v-row>
                <v-form ref="buscarForm">
                  <v-row>
                    <v-col cols="12" md="4" class="mt-2">
                      <v-select
                        v-model="procesoSeleccionado"
                        :items="itemsProcesos"
                        item-text="nombre"
                        item-value="codigo"
                        label="Proceso"
                        :loading="loadingProcesos"
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="mt-2">
                      <v-select
                        v-model="tipoDocumentoSeleccionado"
                        :items="itemsTipoArchivo"
                        item-text="nombre"
                        item-value="codigo"
                        label="Tipo"
                        :loading="loadingTipos"
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="mt-2 d-flex flex-wrap align-center">
                      <v-btn
                        :disabled="!puedeHabilitarBoton"
                        :loading="loadingBuscarArchivos"
                        color="secondary"
                        class="mr-2 mb-6"
                        @click="obtenerArchivosPublicos(tipoDocumentoSeleccionado, procesoSeleccionado)"
                      >
                        Buscar
                        <v-icon class="ml-2">
                          search
                        </v-icon>
                      </v-btn>
                      <v-btn
                        color="blue-grey"
                        class="white--text mb-6"
                        @click="limpiar"
                      >
                        Limpiar
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
                <v-row>
                  <v-col cols="12" class="mt-2">
                    <v-data-table
                      :headers="headers"
                      :items="files"
                      :loading="loadingBuscarArchivos"
                      :search="buscar"
                      loading-text="Cargando datos de archivos..."
                    >
                      <template #top>
                        <v-toolbar color="secondary" flat>
                          <v-toolbar-title class="white--text">
                            Archivos disponibles
                          </v-toolbar-title>
                        </v-toolbar>
                        <v-spacer />
                        <v-text-field
                          v-if="files?.length"
                          v-model="buscar"
                          prepend-icon="$magnify"
                          label="Filtrar datos"
                          single-line
                          hide-details
                          class="mx-4"
                        />
                      </template>
                      <template #item.accion="{ item }">
                        <v-btn color="primary" @click="downloadFile(item)">
                          <v-icon class="mr-2">
                            cloud_download
                          </v-icon>
                          Descargar
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <d-notifier />
    </v-main>
    <d-footer />
  </v-app>
</template>

<script>
const DHeader = () => import('@demre/client-common/src/components/header');
const DFooter = () => import('@demre/client-common/src/components/footer');
const DNotifier = () => import('@demre/client-common/src/components/notifier');
const { getAllURLsArchivos, getGtoken } = require('../utils/archivos');

export default {
  components: {
    DHeader,
    DFooter,
    DNotifier,
  },
  data: () => ({
    itemsProcesos: [],
    itemsTipoArchivo: [],
    files: [],
    gtoken: null,
    procesoSeleccionado: null,
    tipoDocumentoSeleccionado: null,
    loadingBuscarArchivos: false,
    loadingProcesos: false,
    loadingTipos: false,
    buscar: '',
  }),
  computed: {
    headers: () => [
      { text: 'Proceso', value: 'procesoNombre.nombre' },
      { text: 'Tipo', value: 'tipoArchivoNombre.nombre' },
      { text: 'Nombre', value: 'nombre' },
      { text: 'Descripción', value: 'descripcion' },
      { text: 'Acción', value: 'accion', align: 'center', sortable: false, filterable: false }
    ],
    recaptchaPublicKey() {
      return process.env.VUE_APP_RECAPTCHA_PUBLIC_KEY;
    },
    urlCondicionesUso: () => 'https://demre.cl/portales/condiciones-uso-informacion-datos-abiertos',
    puedeHabilitarBoton() {
      return (
        (this.procesoSeleccionado || this.tipoDocumentoSeleccionado) && !this.loadingBuscarArchivos
      );
    },
  },
  mounted() {
    const scriptTag = document.createElement('script');
    scriptTag.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaPublicKey}`;
    scriptTag.onload = () => {};
    scriptTag.onerror = () => {
      this.$store.commit('error-notification', 'Error al cargar reCAPTCHA.');
    };
    document.getElementsByTagName('head')[0].appendChild(scriptTag);
    this.obtenerProcesosPublicos();
    this.obtenerTiposArchivos();
  },
  methods: {
    async downloadFile(item) {
      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_HOST}/archivos-publicos/vigencia-archivo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ codigo: item.codigo })
        });

        if (!response.ok) {
          throw new Error('Error al realizar la petición');
        }
        const { errorControlado } = await response.json();
        if ( errorControlado ) {
          throw new Error(errorControlado);
        }
        window.open(item.url);
      } catch (error) {
        this.$store.commit('error-notification', error.message);
      }
    },
    async obtenerArchivosPublicos() {
      this.loadingBuscarArchivos = true;
      this.files = [];
      try {
        const gToken = await getGtoken(this.recaptchaPublicKey, 'search');
        const response = await fetch(`${process.env.VUE_APP_SERVER_HOST}/archivos-publicos/obtener-archivos-publicos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ proceso: this.procesoSeleccionado, tipoDocumento: this.tipoDocumentoSeleccionado, gToken })
        });

        if (!response.ok) {
          throw new Error('Error al realizar la petición');
        }

        const { archivosPublicos, errorControlado } = await response.json();

        if ( errorControlado ) {
          throw new Error(errorControlado);
        }

        if (!archivosPublicos?.length) {
          throw new Error('No existen archivos para su busqueda');
        }
        this.files = await getAllURLsArchivos(archivosPublicos);

      } catch (error) {
        this.$store.commit('error-notification', error.message);
        this.files = [];
      } finally {
        this.loadingBuscarArchivos = false;
      }
    },
    async obtenerProcesosPublicos() {
      this.loadingProcesos = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_HOST}/archivos-publicos/obtener-procesos`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const { procesosPublicos, errorControlado } = await response.json();
        if ( errorControlado ) {
          throw new Error(errorControlado);
        }
        this.itemsProcesos = procesosPublicos;
      } catch (error) {
        this.$store.commit('error-notification', error.message);
      } finally {
        this.loadingProcesos = false;
      }
    },
    async obtenerTiposArchivos() {
      this.loadingTipos = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_HOST}/archivos-publicos/obtener-tipos-archivos`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const { tiposArchivos, errorControlado } = await response.json();

        if ( errorControlado ) {
          throw new Error(errorControlado);
        }
        this.itemsTipoArchivo = tiposArchivos;

      } catch (error) {
        this.$store.commit('error-notification', error.message);
      } finally {
        this.loadingTipos = false;
      }
    },
    limpiar() {
      this.$refs?.buscarForm?.reset();
      this.files = [];
      this.buscar = '';
    }
  }
};
</script>

<style scoped>
  .subrayado {
    text-decoration: underline;
    color: blue;
    cursor: pointer;
    font-weight: bold;
  }
</style>
